import { ComponentController, defineProp, Component, defineElement } from 'maverick.js/element';
import { signal, peek, effect, getScope, scoped, createContext, useContext, StoreFactory, tick, onDispose, computed } from 'maverick.js';
import { EventsTarget, DOMEvent, listenEvent, isUndefined, isFunction, waitTimeout, isKeyboardClick, setAttribute, isArray, isKeyboardEvent, appendTriggerEvent, isNull, deferredPromise, isString, isNumber, noop, animationFrameThrottle, setStyle } from 'maverick.js/std';
import { i as isHTMLMediaElement } from './providers/type-check.js';
import { A as AudioProviderLoader } from './providers/audio/loader.js';
import { H as HLSProviderLoader } from './providers/hls/loader.js';
import { V as VideoProviderLoader } from './providers/video/loader.js';

const LIST_ADD = Symbol(0);
const LIST_REMOVE = Symbol(0);
const LIST_RESET = Symbol(0);
const LIST_SELECT = Symbol(0);
const LIST_READONLY = Symbol(0);
const LIST_SET_READONLY = Symbol(0);
const LIST_ON_RESET = Symbol(0);
const LIST_ON_REMOVE = Symbol(0);
const LIST_ON_USER_SELECT = Symbol(0);

class List extends EventsTarget {
  a = [];
  /* @internal */
  [LIST_READONLY] = false;
  get length() {
    return this.a.length;
  }
  get readonly() {
    return this[LIST_READONLY];
  }
  /**
   * Transform list to an array.
   */
  toArray() {
    return [...this.a];
  }
  [Symbol.iterator]() {
    return this.a.values();
  }
  /* @internal */
  [LIST_ADD](item, trigger) {
    const index = this.a.length;
    if (!("" + index in this)) {
      Object.defineProperty(this, index, {
        get() {
          return this.a[index];
        }
      });
    }
    if (this.a.includes(item))
      return;
    this.a.push(item);
    this.dispatchEvent(new DOMEvent("add", { detail: item, trigger }));
  }
  /* @internal */
  [LIST_REMOVE](item, trigger) {
    const index = this.a.indexOf(item);
    if (index >= 0) {
      this[LIST_ON_REMOVE]?.(item, trigger);
      this.a.splice(index, 1);
      this.dispatchEvent(new DOMEvent("remove", { detail: item, trigger }));
    }
  }
  /* @internal */
  [LIST_RESET](trigger) {
    for (const item of [...this.a])
      this[LIST_REMOVE](item, trigger);
    this.a = [];
    this[LIST_SET_READONLY](false, trigger);
    this[LIST_ON_RESET]?.();
  }
  /* @internal */
  [LIST_SET_READONLY](readonly, trigger) {
    if (this[LIST_READONLY] === readonly)
      return;
    this[LIST_READONLY] = readonly;
    this.dispatchEvent(new DOMEvent("readonly-change", { detail: readonly, trigger }));
  }
}

var key = {
  fullscreenEnabled: 0,
  fullscreenElement: 1,
  requestFullscreen: 2,
  exitFullscreen: 3,
  fullscreenchange: 4,
  fullscreenerror: 5,
  fullscreen: 6
};
var webkit = [
  "webkitFullscreenEnabled",
  "webkitFullscreenElement",
  "webkitRequestFullscreen",
  "webkitExitFullscreen",
  "webkitfullscreenchange",
  "webkitfullscreenerror",
  "-webkit-full-screen"
];
var moz = [
  "mozFullScreenEnabled",
  "mozFullScreenElement",
  "mozRequestFullScreen",
  "mozCancelFullScreen",
  "mozfullscreenchange",
  "mozfullscreenerror",
  "-moz-full-screen"
];
var ms = [
  "msFullscreenEnabled",
  "msFullscreenElement",
  "msRequestFullscreen",
  "msExitFullscreen",
  "MSFullscreenChange",
  "MSFullscreenError",
  "-ms-fullscreen"
];
var document$1 = typeof window !== "undefined" && typeof window.document !== "undefined" ? window.document : {};
var vendor = "fullscreenEnabled" in document$1 && Object.keys(key) || webkit[0] in document$1 && webkit || moz[0] in document$1 && moz || ms[0] in document$1 && ms || [];
var fscreen = {
  requestFullscreen: function(element) {
    return element[vendor[key.requestFullscreen]]();
  },
  requestFullscreenFunction: function(element) {
    return element[vendor[key.requestFullscreen]];
  },
  get exitFullscreen() {
    return document$1[vendor[key.exitFullscreen]].bind(document$1);
  },
  get fullscreenPseudoClass() {
    return ":" + vendor[key.fullscreen];
  },
  addEventListener: function(type, handler, options) {
    return document$1.addEventListener(vendor[key[type]], handler, options);
  },
  removeEventListener: function(type, handler, options) {
    return document$1.removeEventListener(vendor[key[type]], handler, options);
  },
  get fullscreenEnabled() {
    return Boolean(document$1[vendor[key.fullscreenEnabled]]);
  },
  set fullscreenEnabled(val) {
  },
  get fullscreenElement() {
    return document$1[vendor[key.fullscreenElement]];
  },
  set fullscreenElement(val) {
  },
  get onfullscreenchange() {
    return document$1[("on" + vendor[key.fullscreenchange]).toLowerCase()];
  },
  set onfullscreenchange(handler) {
    return document$1[("on" + vendor[key.fullscreenchange]).toLowerCase()] = handler;
  },
  get onfullscreenerror() {
    return document$1[("on" + vendor[key.fullscreenerror]).toLowerCase()];
  },
  set onfullscreenerror(handler) {
    return document$1[("on" + vendor[key.fullscreenerror]).toLowerCase()] = handler;
  }
};
var fscreen$1 = fscreen;

const CAN_FULLSCREEN = fscreen$1.fullscreenEnabled;
class FullscreenController extends ComponentController {
  /**
   * Tracks whether we're the active fullscreen event listener. Fullscreen events can only be
   * listened to globally on the document so we need to know if they relate to the current host
   * element or not.
   */
  b = false;
  c = false;
  get active() {
    return this.c;
  }
  get supported() {
    return CAN_FULLSCREEN;
  }
  onConnect() {
    listenEvent(fscreen$1, "fullscreenchange", this.d.bind(this));
    listenEvent(fscreen$1, "fullscreenerror", this.e.bind(this));
  }
  async onDisconnect() {
    if (CAN_FULLSCREEN)
      await this.exit();
  }
  d(event) {
    const active = isFullscreen(this.el);
    if (active === this.c)
      return;
    if (!active)
      this.b = false;
    this.c = active;
    this.dispatch("fullscreen-change", { detail: active, trigger: event });
  }
  e(event) {
    if (!this.b)
      return;
    this.dispatch("fullscreen-error", { detail: null, trigger: event });
    this.b = false;
  }
  async enter() {
    try {
      this.b = true;
      if (!this.el || isFullscreen(this.el))
        return;
      assertFullscreenAPI();
      return fscreen$1.requestFullscreen(this.el);
    } catch (error) {
      this.b = false;
      throw error;
    }
  }
  async exit() {
    if (!this.el || !isFullscreen(this.el))
      return;
    assertFullscreenAPI();
    return fscreen$1.exitFullscreen();
  }
}
function canFullscreen() {
  return CAN_FULLSCREEN;
}
function isFullscreen(host) {
  if (fscreen$1.fullscreenElement === host)
    return true;
  try {
    return host.matches(
      // @ts-expect-error - `fullscreenPseudoClass` is missing from `@types/fscreen`.
      fscreen$1.fullscreenPseudoClass
    );
  } catch (error) {
    return false;
  }
}
function assertFullscreenAPI() {
  if (CAN_FULLSCREEN)
    return;
  throw Error(
    "[vidstack] no fullscreen API"
  );
}

const UA = navigator?.userAgent.toLowerCase();
const IS_IOS = /iphone|ipad|ipod|ios|crios|fxios/i.test(UA);
const IS_IPHONE = /(iphone|ipod)/gi.test(navigator?.platform);
const IS_CHROME = !!window.chrome;
const IS_SAFARI = !!window.safari || IS_IOS;
function canOrientScreen() {
  return !isUndefined(screen.orientation) && isFunction(screen.orientation.lock) && isFunction(screen.orientation.unlock);
}
function canPlayHLSNatively(video) {
  if (!video)
    video = document.createElement("video");
  return video.canPlayType("application/vnd.apple.mpegurl").length > 0;
}
function canUsePictureInPicture(video) {
  return !!document.pictureInPictureEnabled && !video.disablePictureInPicture;
}
function canUseVideoPresentation(video) {
  return isFunction(video.webkitSupportsPresentationMode) && isFunction(video.webkitSetPresentationMode);
}
async function canChangeVolume() {
  const video = document.createElement("video");
  video.volume = 0.5;
  await waitTimeout(0);
  return video.volume === 0.5;
}
function getMediaSource() {
  return window?.MediaSource ?? window?.WebKitMediaSource;
}
function getSourceBuffer() {
  return window?.SourceBuffer ?? window?.WebKitSourceBuffer;
}
function isHLSSupported() {
  const MediaSource = getMediaSource();
  if (isUndefined(MediaSource))
    return false;
  const isTypeSupported = MediaSource && isFunction(MediaSource.isTypeSupported) && MediaSource.isTypeSupported('video/mp4; codecs="avc1.42E01E,mp4a.40.2"');
  const SourceBuffer = getSourceBuffer();
  const isSourceBufferValid = isUndefined(SourceBuffer) || !isUndefined(SourceBuffer.prototype) && isFunction(SourceBuffer.prototype.appendBuffer) && isFunction(SourceBuffer.prototype.remove);
  return !!isTypeSupported && !!isSourceBufferValid;
}

const CAN_USE_SCREEN_ORIENTATION_API = canOrientScreen();
class ScreenOrientationController extends ComponentController {
  g = signal(getScreenOrientation());
  f = signal(false);
  h;
  /**
   * The current screen orientation type.
   *
   * @signal
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/ScreenOrientation}
   * @see https://w3c.github.io/screen-orientation/#screen-orientation-types-and-locks
   */
  get type() {
    return this.g();
  }
  /**
   * Whether the screen orientation is currently locked.
   *
   * @signal
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/ScreenOrientation}
   * @see https://w3c.github.io/screen-orientation/#screen-orientation-types-and-locks
   */
  get locked() {
    return this.f();
  }
  /**
   * Whether the viewport is in a portrait orientation.
   *
   * @signal
   */
  get portrait() {
    return this.g().startsWith("portrait");
  }
  /**
   * Whether the viewport is in a landscape orientation.
   *
   * @signal
   */
  get landscape() {
    return this.g().startsWith("landscape");
  }
  /**
   * Whether the native Screen Orientation API is available.
   */
  get supported() {
    return CAN_USE_SCREEN_ORIENTATION_API;
  }
  onConnect() {
    if (CAN_USE_SCREEN_ORIENTATION_API) {
      listenEvent(screen.orientation, "change", this.i.bind(this));
    } else {
      const query = window.matchMedia("(orientation: landscape)");
      query.onchange = this.i.bind(this);
      return () => query.onchange = null;
    }
  }
  async onDisconnect() {
    if (CAN_USE_SCREEN_ORIENTATION_API && this.f())
      await this.unlock();
  }
  i(event) {
    this.g.set(getScreenOrientation());
    this.dispatch("orientation-change", {
      detail: {
        orientation: peek(this.g),
        lock: this.h
      },
      trigger: event
    });
  }
  /**
   * Locks the orientation of the screen to the desired orientation type using the
   * Screen Orientation API.
   *
   * @param lockType - The screen lock orientation type.
   * @throws Error - If screen orientation API is unavailable.
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Screen/orientation}
   * @see {@link https://w3c.github.io/screen-orientation}
   */
  async lock(lockType) {
    if (peek(this.f) || this.h === lockType)
      return;
    assertScreenOrientationAPI();
    await screen.orientation.lock(lockType);
    this.f.set(true);
    this.h = lockType;
  }
  /**
   * Unlocks the orientation of the screen to it's default state using the Screen Orientation
   * API. This method will throw an error if the API is unavailable.
   *
   * @throws Error - If screen orientation API is unavailable.
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Screen/orientation}
   * @see {@link https://w3c.github.io/screen-orientation}
   */
  async unlock() {
    if (!peek(this.f))
      return;
    assertScreenOrientationAPI();
    this.h = void 0;
    await screen.orientation.unlock();
    this.f.set(false);
  }
}
function assertScreenOrientationAPI() {
  if (CAN_USE_SCREEN_ORIENTATION_API)
    return;
  throw Error(
    "[vidstack] no orientation API"
  );
}
function getScreenOrientation() {
  if (CAN_USE_SCREEN_ORIENTATION_API)
    return window.screen.orientation.type;
  return window.innerWidth >= window.innerHeight ? "landscape-primary" : "portrait-primary";
}

function setAttributeIfEmpty(target, name, value) {
  if (!target.hasAttribute(name))
    target.setAttribute(name, value);
}
function setARIALabel(target, label) {
  if (target.hasAttribute("aria-label") || target.hasAttribute("aria-describedby"))
    return;
  function updateAriaDescription() {
    setAttribute(target, "aria-label", label());
  }
  effect(updateAriaDescription);
}
function isElementParent(owner, node, test) {
  while (node) {
    if (node === owner) {
      return true;
    } else if (node.localName === owner.localName || test?.(node)) {
      break;
    } else {
      node = node.parentElement;
    }
  }
  return false;
}
function onPress(target, handler) {
  listenEvent(target, "pointerup", (event) => {
    if (event.button === 0)
      handler(event);
  });
  listenEvent(target, "keydown", (event) => {
    if (isKeyboardClick(event))
      handler(event);
  });
}
function scopedRaf(callback) {
  const scope = getScope();
  requestAnimationFrame(() => scoped(callback, scope));
}

const mediaContext = createContext();
function useMedia() {
  return useContext(mediaContext);
}

const MEDIA_ATTRIBUTES = [
  "autoplay",
  "autoplayError",
  "canFullscreen",
  "canPictureInPicture",
  "canLoad",
  "canPlay",
  "canSeek",
  "ended",
  "error",
  "fullscreen",
  "loop",
  "live",
  "liveEdge",
  "mediaType",
  "muted",
  "paused",
  "pictureInPicture",
  "playing",
  "playsinline",
  "seeking",
  "started",
  "streamType",
  "userIdle",
  "viewType",
  "waiting"
];

const MEDIA_KEY_SHORTCUTS = {
  togglePaused: "k Space",
  toggleMuted: "m",
  toggleFullscreen: "f",
  togglePictureInPicture: "i",
  toggleCaptions: "c",
  seekBackward: "ArrowLeft",
  seekForward: "ArrowRight",
  volumeUp: "ArrowUp",
  volumeDown: "ArrowDown"
};
const MODIFIER_KEYS = /* @__PURE__ */ new Set(["Shift", "Alt", "Meta", "Control"]), BUTTON_SELECTORS = 'button, [role="button"]', IGNORE_SELECTORS = 'input, textarea, select, [contenteditable], [role^="menuitem"]';
class MediaKeyboardController extends ComponentController {
  constructor(instance, _media) {
    super(instance);
    this.k = _media;
  }
  onConnect() {
    effect(this.Xa.bind(this));
  }
  Xa() {
    const { keyDisabled, keyTarget } = this.$props;
    if (keyDisabled())
      return;
    const target = keyTarget() === "player" ? this.el : document, $active = signal(false);
    if (target === this.el) {
      this.listen("focusin", () => $active.set(true));
      this.listen("focusout", (event) => {
        if (!this.el.contains(event.target))
          $active.set(false);
      });
    } else {
      if (!peek($active))
        $active.set(document.querySelector("media-player") === this.el);
      listenEvent(document, "focusin", (event) => {
        const activePlayer = event.composedPath().find((el) => el instanceof Element && el.localName === "media-player");
        if (activePlayer !== void 0)
          $active.set(this.el === activePlayer);
      });
    }
    effect(() => {
      if (!$active())
        return;
      listenEvent(target, "keyup", this.Ya.bind(this));
      listenEvent(target, "keydown", this.Za.bind(this));
      listenEvent(target, "keydown", this._a.bind(this), { capture: true });
    });
  }
  Ya(event) {
    const focused = document.activeElement, sliderFocused = focused?.hasAttribute("data-media-slider");
    if (!event.key || !this.$store.canSeek() || sliderFocused || focused?.matches(IGNORE_SELECTORS)) {
      return;
    }
    const method = this.Va(event);
    if (method?.startsWith("seek")) {
      event.preventDefault();
      event.stopPropagation();
      if (this.Ta) {
        this.Wa(event);
        this.Ta = null;
      } else {
        this.k.remote.seek(this.Ua, event);
        this.Ua = void 0;
      }
    }
    if (method?.startsWith("volume")) {
      const volumeSlider = this.el.querySelector("media-volume-slider");
      volumeSlider?.dispatchEvent(new DOMEvent("keyup", { trigger: event }));
    }
  }
  Za(event) {
    if (!event.key || MODIFIER_KEYS.has(event.key))
      return;
    const focused = document.activeElement;
    if (focused?.matches(IGNORE_SELECTORS) || isKeyboardClick(event) && focused?.matches(BUTTON_SELECTORS)) {
      return;
    }
    const sliderFocused = focused?.hasAttribute("data-media-slider"), method = this.Va(event);
    if (!method && !event.metaKey && /[0-9]/.test(event.key) && !sliderFocused) {
      event.preventDefault();
      event.stopPropagation();
      this.k.remote.seek(this.$store.duration() / 10 * Number(event.key), event);
      return;
    }
    if (!method || /volume|seek/.test(method) && sliderFocused)
      return;
    event.preventDefault();
    event.stopPropagation();
    switch (method) {
      case "seekForward":
      case "seekBackward":
        this.$a(event, method);
        break;
      case "volumeUp":
      case "volumeDown":
        const volumeSlider = this.el.querySelector("media-volume-slider");
        if (volumeSlider) {
          volumeSlider.dispatchEvent(new DOMEvent("keydown", { trigger: event }));
        } else {
          const value = event.shiftKey ? 0.1 : 0.05;
          this.k.remote.changeVolume(
            this.$store.volume() + (method === "volumeUp" ? +value : -value),
            event
          );
        }
        break;
      case "toggleFullscreen":
        this.k.remote.toggleFullscreen("prefer-media", event);
        break;
      default:
        this.k.remote[method]?.(event);
    }
  }
  _a(event) {
    if (isHTMLMediaElement(event.target) && this.Va(event)) {
      event.preventDefault();
    }
  }
  Va(event) {
    const keyShortcuts = {
      ...this.$props.keyShortcuts(),
      ...this.k.ariaKeys
    };
    return Object.keys(keyShortcuts).find(
      (method) => keyShortcuts[method].split(" ").some(
        (keys) => replaceSymbolKeys(keys).replace(/Control/g, "Ctrl").split("+").every(
          (key) => MODIFIER_KEYS.has(key) ? event[key.toLowerCase() + "Key"] : event.key === key.replace("Space", " ")
        )
      )
    );
  }
  Ua;
  ab(event, type) {
    const seekBy = event.shiftKey ? 10 : 5;
    return this.Ua = Math.max(
      0,
      Math.min(
        (this.Ua ?? this.$store.currentTime()) + (type === "seekForward" ? +seekBy : -seekBy),
        this.$store.duration()
      )
    );
  }
  Ta = null;
  Wa(event) {
    this.Ta?.dispatchEvent(new DOMEvent(event.type, { trigger: event }));
  }
  $a(event, type) {
    if (!this.$store.canSeek())
      return;
    if (!this.Ta)
      this.Ta = this.el.querySelector("media-time-slider");
    if (this.Ta) {
      this.Wa(event);
    } else {
      this.k.remote.seeking(this.ab(event, type), event);
    }
  }
}
const SYMBOL_KEY_MAP = ["!", "@", "#", "$", "%", "^", "&", "*", "(", ")"];
function replaceSymbolKeys(key) {
  return key.replace(/Shift\+(\d)/g, (_, num) => SYMBOL_KEY_MAP[num - 1]);
}

const mediaPlayerProps = {
  autoplay: false,
  aspectRatio: defineProp({
    value: null,
    type: {
      from(value) {
        if (!value)
          return null;
        if (!value.includes("/"))
          return +value;
        const [width, height] = value.split("/").map(Number);
        return +(width / height).toFixed(4);
      }
    }
  }),
  controls: false,
  currentTime: 0,
  crossorigin: null,
  fullscreenOrientation: "landscape",
  load: "visible",
  logLevel: "silent",
  loop: false,
  muted: false,
  paused: true,
  playsinline: false,
  playbackRate: 1,
  poster: "",
  preload: "metadata",
  preferNativeHLS: defineProp({
    value: false,
    attribute: "prefer-native-hls"
  }),
  src: "",
  userIdleDelay: 2e3,
  viewType: "unknown",
  streamType: "unknown",
  volume: 1,
  liveEdgeTolerance: 10,
  minLiveDVRWindow: 60,
  keyDisabled: false,
  keyTarget: "player",
  keyShortcuts: MEDIA_KEY_SHORTCUTS,
  title: "",
  thumbnails: null,
  textTracks: defineProp({
    value: [],
    attribute: false
  }),
  smallBreakpointX: 600,
  largeBreakpointX: 980,
  smallBreakpointY: 380,
  largeBreakpointY: 600
};

class TimeRange {
  W;
  get length() {
    return this.W.length;
  }
  constructor(start, end) {
    if (isArray(start)) {
      this.W = start;
    } else if (!isUndefined(start) && !isUndefined(end)) {
      this.W = [[start, end]];
    } else {
      this.W = [];
    }
  }
  start(index) {
    return this.W[index][0] ?? Infinity;
  }
  end(index) {
    return this.W[index][1] ?? Infinity;
  }
}
function getTimeRangesStart(range) {
  if (!range.length)
    return null;
  let min = range.start(0);
  for (let i = 1; i < range.length; i++) {
    const value = range.start(i);
    if (value < min)
      min = value;
  }
  return min;
}
function getTimeRangesEnd(range) {
  if (!range.length)
    return null;
  let max = range.end(0);
  for (let i = 1; i < range.length; i++) {
    const value = range.end(i);
    if (value > max)
      max = value;
  }
  return max;
}

const MediaStoreFactory = new StoreFactory({
  audioTracks: [],
  audioTrack: null,
  autoplay: false,
  autoplayError: void 0,
  buffered: new TimeRange(),
  duration: 0,
  canLoad: false,
  canFullscreen: false,
  canPictureInPicture: false,
  canPlay: false,
  controls: false,
  crossorigin: null,
  poster: "",
  currentTime: 0,
  ended: false,
  error: void 0,
  fullscreen: false,
  loop: false,
  logLevel: "silent",
  mediaType: "unknown",
  muted: false,
  paused: true,
  played: new TimeRange(),
  playing: false,
  playsinline: false,
  pictureInPicture: false,
  preload: "metadata",
  playbackRate: 1,
  qualities: [],
  quality: null,
  autoQuality: false,
  canSetQuality: true,
  seekable: new TimeRange(),
  seeking: false,
  source: { src: "", type: "" },
  sources: [],
  started: false,
  title: "",
  textTracks: [],
  textTrack: null,
  thumbnails: null,
  thumbnailCues: [],
  volume: 1,
  waiting: false,
  get viewType() {
    return this.providedViewType !== "unknown" ? this.providedViewType : this.mediaType;
  },
  get streamType() {
    return this.providedStreamType !== "unknown" ? this.providedStreamType : this.inferredStreamType;
  },
  get currentSrc() {
    return this.source;
  },
  get bufferedStart() {
    return getTimeRangesStart(this.buffered) ?? 0;
  },
  get bufferedEnd() {
    return getTimeRangesEnd(this.buffered) ?? 0;
  },
  get seekableStart() {
    return getTimeRangesStart(this.seekable) ?? 0;
  },
  get seekableEnd() {
    return this.canPlay ? getTimeRangesEnd(this.seekable) ?? Infinity : 0;
  },
  get seekableWindow() {
    return Math.max(0, this.seekableEnd - this.seekableStart);
  },
  // ~~ responsive design ~~
  touchPointer: false,
  orientation: "landscape",
  mediaWidth: 0,
  mediaHeight: 0,
  breakpointX: "sm",
  breakpointY: "sm",
  // ~~ user props ~~
  userIdle: false,
  userBehindLiveEdge: false,
  // ~~ live props ~~
  liveEdgeTolerance: 10,
  minLiveDVRWindow: 60,
  get canSeek() {
    return /unknown|on-demand|:dvr/.test(this.streamType) && Number.isFinite(this.seekableWindow) && (!this.live || /:dvr/.test(this.streamType) && this.seekableWindow >= this.minLiveDVRWindow);
  },
  get live() {
    return this.streamType.includes("live") || !Number.isFinite(this.duration);
  },
  get liveEdgeStart() {
    return this.live && Number.isFinite(this.seekableEnd) ? Math.max(0, (this.liveSyncPosition ?? this.seekableEnd) - this.liveEdgeTolerance) : 0;
  },
  get liveEdge() {
    return this.live && (!this.canSeek || !this.userBehindLiveEdge && this.currentTime >= this.liveEdgeStart);
  },
  get liveEdgeWindow() {
    return this.live && Number.isFinite(this.seekableEnd) ? this.seekableEnd - this.liveEdgeStart : 0;
  },
  // ~~ internal props ~~
  autoplaying: false,
  providedViewType: "unknown",
  providedStreamType: "unknown",
  inferredStreamType: "unknown",
  liveSyncPosition: null
});
const DO_NOT_RESET_ON_SRC_CHANGE = /* @__PURE__ */ new Set([
  "autoplay",
  "breakpointX",
  "breakpointY",
  "canFullscreen",
  "canLoad",
  "canPictureInPicture",
  "controls",
  "fullscreen",
  "logLevel",
  "loop",
  "mediaHeight",
  "mediaWidth",
  "muted",
  "orientation",
  "pictureInPicture",
  "playsinline",
  "poster",
  "preload",
  "providedStreamType",
  "providedViewType",
  "source",
  "sources",
  "textTrack",
  "textTracks",
  "thumbnailCues",
  "thumbnails",
  "title",
  "touchPointer",
  "volume"
]);
function softResetMediaStore($media) {
  MediaStoreFactory.reset($media, (prop) => !DO_NOT_RESET_ON_SRC_CHANGE.has(prop));
  tick();
}

const SELECTED = Symbol(0);
class SelectList extends List {
  get selected() {
    return this.a.find((item) => item.selected) ?? null;
  }
  get selectedIndex() {
    return this.a.findIndex((item) => item.selected);
  }
  /* @internal */
  [LIST_ON_REMOVE](item, trigger) {
    this[LIST_SELECT](item, false, trigger);
  }
  /* @internal */
  [LIST_ADD](item, trigger) {
    item[SELECTED] = false;
    Object.defineProperty(item, "selected", {
      get() {
        return this[SELECTED];
      },
      set: (selected) => {
        if (this.readonly)
          return;
        this[LIST_ON_USER_SELECT]?.();
        this[LIST_SELECT](item, selected);
      }
    });
    super[LIST_ADD](item, trigger);
  }
  /* @internal */
  [LIST_SELECT](item, selected, trigger) {
    if (selected === item[SELECTED])
      return;
    const prev = this.selected;
    item[SELECTED] = selected;
    const changed = !selected ? prev === item : prev !== item;
    if (changed) {
      if (prev)
        prev[SELECTED] = false;
      this.dispatchEvent(
        new DOMEvent("change", {
          detail: { prev, current: this.selected },
          trigger
        })
      );
    }
  }
}

const SET_AUTO_QUALITY = Symbol(0);
const ENABLE_AUTO_QUALITY = Symbol(0);

class VideoQualityList extends SelectList {
  Sa = false;
  /**
   * Configures quality switching:
   *
   * - `current`: Trigger an immediate quality level switch. This will abort the current fragment
   * request if any, flush the whole buffer, and fetch fragment matching with current position
   * and requested quality level.
   *
   * - `next`: Trigger a quality level switch for next fragment. This could eventually flush
   * already buffered next fragment.
   *
   * - `load`: Set quality level for next loaded fragment.
   *
   * @see {@link https://vidstack.io/docs/player/core-concepts/quality#switch}
   * @see {@link https://github.com/video-dev/hls.js/blob/master/docs/API.md#quality-switch-control-api}
   */
  switch = "current";
  /**
   * Whether automatic quality selection is enabled.
   */
  get auto() {
    return this.Sa || this.readonly;
  }
  /* @internal */
  [ENABLE_AUTO_QUALITY];
  /* @internal */
  [LIST_ON_USER_SELECT]() {
    this[SET_AUTO_QUALITY](false);
  }
  /* @internal */
  [LIST_ON_RESET](trigger) {
    this[SET_AUTO_QUALITY](false, trigger);
  }
  /**
   * Request automatic quality selection (if supported). This will be a no-op if the list is
   * `readonly` as that already implies auto-selection.
   */
  autoSelect(trigger) {
    if (this.readonly || this.Sa || !this[ENABLE_AUTO_QUALITY])
      return;
    this[ENABLE_AUTO_QUALITY]();
    this[SET_AUTO_QUALITY](true, trigger);
  }
  /* @internal */
  [SET_AUTO_QUALITY](auto, trigger) {
    if (this.Sa === auto)
      return;
    this.Sa = auto;
    this.dispatchEvent(
      new DOMEvent("auto-change", {
        detail: auto,
        trigger
      })
    );
  }
}

class MediaLoadController extends ComponentController {
  constructor(instance, _callback) {
    super(instance);
    this._e = _callback;
  }
  async onAttach(el) {
    const load = this.$props.load();
    if (load === "eager") {
      requestAnimationFrame(this._e);
    } else if (load === "idle") {
      const { waitIdlePeriod } = await import('maverick.js/std');
      waitIdlePeriod(this._e);
    } else if (load === "visible") {
      const observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          observer.disconnect();
          this._e();
        }
      });
      observer.observe(el);
      return observer.disconnect.bind(observer);
    }
  }
}

class MediaPlayerDelegate {
  constructor(_handle, _media) {
    this.I = _handle;
    this.k = _media;
  }
  R(type, ...init) {
    this.I(new DOMEvent(type, init?.[0]));
  }
  async af(info, trigger) {
    const { $store, logger } = this.k;
    if (peek($store.canPlay))
      return;
    this.R("can-play", { detail: info, trigger });
    tick();
    if ($store.canPlay() && $store.autoplay() && !$store.started()) {
      await this.$e();
    }
  }
  async $e() {
    const { player, $store } = this.k;
    $store.autoplaying.set(true);
    try {
      await player.play();
      this.R("autoplay", { detail: { muted: $store.muted() } });
    } catch (error) {
      this.R("autoplay-fail", {
        detail: {
          muted: $store.muted(),
          error
        }
      });
    } finally {
      $store.autoplaying.set(false);
    }
  }
}

class Queue {
  Pe = /* @__PURE__ */ new Map();
  /**
   * Queue the given `item` under the given `key` to be processed at a later time by calling
   * `serve(key)`.
   */
  n(key, item) {
    if (!this.Pe.has(key))
      this.Pe.set(key, /* @__PURE__ */ new Set());
    this.Pe.get(key).add(item);
  }
  /**
   * Process all items in queue for the given `key`.
   */
  Ue(key, callback) {
    const items = this.Pe.get(key);
    if (items)
      for (const item of items)
        callback(item);
    this.Pe.delete(key);
  }
  /**
   * Removes all queued items under the given `key`.
   */
  of(key) {
    this.Pe.delete(key);
  }
  /**
   * The number of items currently queued under the given `key`.
   */
  Ve(key) {
    return this.Pe.get(key)?.size ?? 0;
  }
  /**
   * Clear all items in the queue.
   */
  Ye() {
    this.Pe.clear();
  }
}

function coerceToError(error) {
  return error instanceof Error ? error : Error(JSON.stringify(error));
}

class MediaUserController extends ComponentController {
  qa = -2;
  oa = 2e3;
  ra = false;
  pa = null;
  /**
   * Whether the media user is currently idle.
   */
  get idling() {
    return this.$store.userIdle();
  }
  /**
   * The amount of delay in milliseconds while media playback is progressing without user
   * activity to indicate an idle state.
   *
   * @defaultValue 2000
   */
  get idleDelay() {
    return this.oa;
  }
  set idleDelay(newDelay) {
    this.oa = newDelay;
  }
  /**
   * Change the user idle state.
   */
  idle(idle, delay = this.oa, trigger) {
    this.sa();
    if (!this.ra)
      this.ta(idle, delay, trigger);
  }
  /**
   * Whether all idle tracking should be paused until resumed again.
   */
  pauseIdleTracking(paused, trigger) {
    this.ra = paused;
    if (paused) {
      this.sa();
      this.ta(false, 0, trigger);
    }
  }
  onConnect() {
    effect(this.x.bind(this));
    listenEvent(this.el, "play", this.va.bind(this));
    listenEvent(this.el, "pause", this.wa.bind(this));
  }
  x() {
    if (this.$store.paused())
      return;
    const onStopIdle = this.xa.bind(this);
    for (const eventType of ["pointerup", "keydown"]) {
      listenEvent(this.el, eventType, onStopIdle);
    }
    effect(() => {
      if (!this.$store.touchPointer())
        listenEvent(this.el, "pointermove", onStopIdle);
    });
  }
  va(event) {
    this.idle(true, this.oa, event);
  }
  wa(event) {
    this.idle(false, 0, event);
  }
  sa() {
    window.clearTimeout(this.qa);
    this.qa = -1;
  }
  xa(event) {
    if (event.MEDIA_GESTURE)
      return;
    if (isKeyboardEvent(event)) {
      if (event.key === "Escape") {
        this.el?.focus();
        this.pa = null;
      } else if (this.pa) {
        event.preventDefault();
        requestAnimationFrame(() => {
          this.pa?.focus();
          this.pa = null;
        });
      }
    }
    this.idle(false, 0, event);
    this.idle(true, this.oa, event);
  }
  ta(idle, delay, trigger) {
    if (delay === 0) {
      this.ua(idle, trigger);
      return;
    }
    this.qa = window.setTimeout(() => {
      this.ua(idle && !this.ra, trigger);
    }, delay);
  }
  ua(idle, trigger) {
    if (this.$store.userIdle() === idle)
      return;
    this.$store.userIdle.set(idle);
    if (idle && document.activeElement && this.el?.contains(document.activeElement)) {
      this.pa = document.activeElement;
      requestAnimationFrame(() => this.el?.focus());
    }
    this.dispatch("user-idle-change", {
      detail: idle,
      trigger
    });
  }
}

class MediaRequestContext {
  $a = false;
  gf = false;
  ef = false;
  Pe = new Queue();
}
class MediaRequestManager extends ComponentController {
  constructor(instance, _stateMgr, _request, _media) {
    super(instance);
    this.o = _stateMgr;
    this.bf = _request;
    this.k = _media;
    this.cf = _media.$store;
    this.j = _media.$provider;
    this.L = new MediaUserController(instance);
    this.df = new FullscreenController(instance);
    this.nb = new ScreenOrientationController(instance);
  }
  L;
  df;
  nb;
  cf;
  j;
  onConnect() {
    effect(this.kf.bind(this));
    effect(this.lf.bind(this));
    effect(this.mf.bind(this));
    const names = Object.getOwnPropertyNames(Object.getPrototypeOf(this)), handle = this.nf.bind(this);
    for (const name of names) {
      if (name.startsWith("media-")) {
        this.listen(name, handle);
      }
    }
    this.listen("fullscreen-change", this.d.bind(this));
  }
  nf(event) {
    event.stopPropagation();
    if (peek(this.j))
      this[event.type]?.(event);
  }
  async H() {
    const { canPlay, paused, ended, autoplaying, seekableStart } = this.cf;
    if (!peek(paused))
      return;
    try {
      const provider = peek(this.j);
      throwIfNotReadyForPlayback(provider, peek(canPlay));
      if (peek(ended)) {
        provider.currentTime = seekableStart() + 0.1;
      }
      return provider.play();
    } catch (error) {
      const errorEvent = this.createEvent("play-fail", { detail: coerceToError(error) });
      errorEvent.autoplay = autoplaying();
      this.o.I(errorEvent);
      throw error;
    }
  }
  async G() {
    const { canPlay, paused } = this.cf;
    if (peek(paused))
      return;
    const provider = peek(this.j);
    throwIfNotReadyForPlayback(provider, peek(canPlay));
    return provider.pause();
  }
  Q() {
    const { canPlay, live, liveEdge, canSeek, liveSyncPosition, seekableEnd, userBehindLiveEdge } = this.cf;
    userBehindLiveEdge.set(false);
    if (peek(() => !live() || liveEdge() || !canSeek()))
      return;
    const provider = peek(this.j);
    throwIfNotReadyForPlayback(provider, peek(canPlay));
    provider.currentTime = liveSyncPosition() ?? seekableEnd() - 2;
  }
  ff = false;
  async M(target = "prefer-media") {
    const provider = peek(this.j);
    const adapter = target === "prefer-media" && this.df.supported || target === "media" ? this.df : provider?.fullscreen;
    throwIfFullscreenNotSupported(target, adapter);
    if (adapter.active)
      return;
    if (peek(this.cf.pictureInPicture)) {
      this.ff = true;
      await this.P();
    }
    return adapter.enter();
  }
  async N(target = "prefer-media") {
    const provider = peek(this.j);
    const adapter = target === "prefer-media" && this.df.supported || target === "media" ? this.df : provider?.fullscreen;
    throwIfFullscreenNotSupported(target, adapter);
    if (!adapter.active)
      return;
    if (this.nb.locked)
      await this.nb.unlock();
    try {
      const result = await adapter.exit();
      if (this.ff && peek(this.cf.canPictureInPicture)) {
        await this.O();
      }
      return result;
    } finally {
      this.ff = false;
    }
  }
  async O() {
    this.hf();
    if (this.cf.pictureInPicture())
      return;
    return await this.j().pictureInPicture.enter();
  }
  async P() {
    this.hf();
    if (!this.cf.pictureInPicture())
      return;
    return await this.j().pictureInPicture.exit();
  }
  hf() {
    if (this.cf.canPictureInPicture())
      return;
    throw Error(
      "[vidstack] no pip support"
    );
  }
  kf() {
    this.L.idleDelay = this.$props.userIdleDelay();
  }
  lf() {
    const { canLoad, canFullscreen } = this.cf, supported = this.df.supported || this.j()?.fullscreen?.supported || false;
    if (canLoad() && peek(canFullscreen) === supported)
      return;
    canFullscreen.set(supported);
  }
  mf() {
    const { canLoad, canPictureInPicture } = this.cf, supported = this.j()?.pictureInPicture?.supported || false;
    if (canLoad() && peek(canPictureInPicture) === supported)
      return;
    canPictureInPicture.set(supported);
  }
  ["media-audio-track-change-request"](event) {
    if (this.k.audioTracks.readonly) {
      return;
    }
    const index = event.detail, track = this.k.audioTracks[index];
    if (track) {
      this.bf.Pe.n("audioTrack", event);
      track.selected = true;
    }
  }
  async ["media-enter-fullscreen-request"](event) {
    try {
      this.bf.Pe.n("fullscreen", event);
      await this.M(event.detail);
    } catch (error) {
      this.e(error);
    }
  }
  async ["media-exit-fullscreen-request"](event) {
    try {
      this.bf.Pe.n("fullscreen", event);
      await this.N(event.detail);
    } catch (error) {
      this.e(error);
    }
  }
  async d(event) {
    if (!event.detail)
      return;
    try {
      const lockType = peek(this.$props.fullscreenOrientation);
      if (this.nb.supported && !isUndefined(lockType)) {
        await this.nb.lock(lockType);
      }
    } catch (e) {
    }
  }
  e(error) {
    this.o.I(
      this.createEvent("fullscreen-error", {
        detail: coerceToError(error)
      })
    );
  }
  async ["media-enter-pip-request"](event) {
    try {
      this.bf.Pe.n("pip", event);
      await this.O();
    } catch (error) {
      this.jf(error);
    }
  }
  async ["media-exit-pip-request"](event) {
    try {
      this.bf.Pe.n("pip", event);
      await this.P();
    } catch (error) {
      this.jf(error);
    }
  }
  jf(error) {
    this.o.I(
      this.createEvent("picture-in-picture-error", {
        detail: coerceToError(error)
      })
    );
  }
  ["media-live-edge-request"](event) {
    const { live, liveEdge, canSeek } = this.cf;
    if (!live() || liveEdge() || !canSeek())
      return;
    this.bf.Pe.n("seeked", event);
    try {
      this.Q();
    } catch (e) {
    }
  }
  ["media-loop-request"]() {
    window.requestAnimationFrame(async () => {
      try {
        this.bf.gf = true;
        this.bf.ef = true;
        await this.H();
      } catch (e) {
        this.bf.gf = false;
        this.bf.ef = false;
      }
    });
  }
  async ["media-pause-request"](event) {
    if (this.cf.paused())
      return;
    try {
      this.bf.Pe.n("pause", event);
      await this.j().pause();
    } catch (e) {
      this.bf.Pe.of("pause");
    }
  }
  async ["media-play-request"](event) {
    if (!this.cf.paused())
      return;
    try {
      this.bf.Pe.n("play", event);
      await this.j().play();
    } catch (e) {
      const errorEvent = this.createEvent("play-fail", { detail: coerceToError(e) });
      this.o.I(errorEvent);
    }
  }
  ["media-rate-change-request"](event) {
    if (this.cf.playbackRate() === event.detail)
      return;
    this.bf.Pe.n("rate", event);
    this.j().playbackRate = event.detail;
  }
  ["media-quality-change-request"](event) {
    if (this.k.qualities.readonly) {
      return;
    }
    this.bf.Pe.n("quality", event);
    const index = event.detail;
    if (index < 0) {
      this.k.qualities.autoSelect(event);
    } else {
      const quality = this.k.qualities[index];
      if (quality) {
        quality.selected = true;
      }
    }
  }
  ["media-resume-user-idle-request"](event) {
    this.bf.Pe.n("userIdle", event);
    this.L.pauseIdleTracking(false, event);
  }
  ["media-pause-user-idle-request"](event) {
    this.bf.Pe.n("userIdle", event);
    this.L.pauseIdleTracking(true, event);
  }
  ["media-seek-request"](event) {
    const { seekableStart, seekableEnd, ended, canSeek, live, userBehindLiveEdge } = this.cf;
    if (ended())
      this.bf.ef = true;
    this.bf.$a = false;
    this.bf.Pe.of("seeking");
    const boundTime = Math.min(Math.max(seekableStart() + 0.1, event.detail), seekableEnd() - 0.1);
    if (!Number.isFinite(boundTime) || !canSeek())
      return;
    this.bf.Pe.n("seeked", event);
    this.j().currentTime = boundTime;
    if (live() && event.isOriginTrusted && Math.abs(seekableEnd() - boundTime) >= 2) {
      userBehindLiveEdge.set(true);
    }
  }
  ["media-seeking-request"](event) {
    this.bf.Pe.n("seeking", event);
    this.cf.seeking.set(true);
    this.bf.$a = true;
  }
  ["media-start-loading"](event) {
    if (this.cf.canLoad())
      return;
    this.bf.Pe.n("load", event);
    this.o.I(this.createEvent("can-load"));
  }
  ["media-text-track-change-request"](event) {
    const { index, mode } = event.detail, track = this.k.textTracks[index];
    if (track) {
      this.bf.Pe.n("textTrack", event);
      track.setMode(mode, event);
    }
  }
  ["media-mute-request"](event) {
    if (this.cf.muted())
      return;
    this.bf.Pe.n("volume", event);
    this.j().muted = true;
  }
  ["media-unmute-request"](event) {
    const { muted, volume } = this.cf;
    if (!muted())
      return;
    this.bf.Pe.n("volume", event);
    this.k.$provider().muted = false;
    if (volume() === 0) {
      this.bf.Pe.n("volume", event);
      this.j().volume = 0.25;
    }
  }
  ["media-volume-change-request"](event) {
    const { muted, volume } = this.cf;
    const newVolume = event.detail;
    if (volume() === newVolume)
      return;
    this.bf.Pe.n("volume", event);
    this.j().volume = newVolume;
    if (newVolume > 0 && muted()) {
      this.bf.Pe.n("volume", event);
      this.j().muted = false;
    }
  }
}
function throwIfNotReadyForPlayback(provider, canPlay) {
  if (provider && canPlay)
    return;
  throw Error(
    "[vidstack] media not ready"
  );
}
function throwIfFullscreenNotSupported(target, fullscreen) {
  if (fullscreen?.supported)
    return;
  throw Error(
    "[vidstack] no fullscreen support"
  );
}

var functionDebounce = debounce;
function debounce(fn, wait, callFirst) {
  var timeout = null;
  var debouncedFn = null;
  var clear = function() {
    if (timeout) {
      clearTimeout(timeout);
      debouncedFn = null;
      timeout = null;
    }
  };
  var flush = function() {
    var call = debouncedFn;
    clear();
    if (call) {
      call();
    }
  };
  var debounceWrapper = function() {
    if (!wait) {
      return fn.apply(this, arguments);
    }
    var context = this;
    var args = arguments;
    var callNow = callFirst && !timeout;
    clear();
    debouncedFn = function() {
      fn.apply(context, args);
    };
    timeout = setTimeout(function() {
      timeout = null;
      if (!callNow) {
        var call = debouncedFn;
        debouncedFn = null;
        return call();
      }
    }, wait);
    if (callNow) {
      return debouncedFn();
    }
  };
  debounceWrapper.cancel = clear;
  debounceWrapper.flush = flush;
  return debounceWrapper;
}

var functionThrottle = throttle;
function throttle(fn, interval, options) {
  var timeoutId = null;
  var throttledFn = null;
  var leading = options && options.leading;
  var trailing = options && options.trailing;
  if (leading == null) {
    leading = true;
  }
  if (trailing == null) {
    trailing = !leading;
  }
  if (leading == true) {
    trailing = false;
  }
  var cancel = function() {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };
  var flush = function() {
    var call = throttledFn;
    cancel();
    if (call) {
      call();
    }
  };
  var throttleWrapper = function() {
    var callNow = leading && !timeoutId;
    var context = this;
    var args = arguments;
    throttledFn = function() {
      return fn.apply(context, args);
    };
    if (!timeoutId) {
      timeoutId = setTimeout(function() {
        timeoutId = null;
        if (trailing) {
          return throttledFn();
        }
      }, interval);
    }
    if (callNow) {
      callNow = false;
      return throttledFn();
    }
  };
  throttleWrapper.cancel = cancel;
  throttleWrapper.flush = flush;
  return throttleWrapper;
}

const ATTACH_VIDEO = Symbol(0);
const TEXT_TRACK_CROSSORIGIN = Symbol(0);
const TEXT_TRACK_READY_STATE = Symbol(0);
const TEXT_TRACK_UPDATE_ACTIVE_CUES = Symbol(0);
const TEXT_TRACK_CAN_LOAD = Symbol(0);
const TEXT_TRACK_ON_MODE_CHANGE = Symbol(0);
const TEXT_TRACK_NATIVE = Symbol(0);
const TEXT_TRACK_NATIVE_HLS = Symbol(0);

const TRACKED_EVENT = /* @__PURE__ */ new Set([
  "autoplay",
  "autoplay-fail",
  "can-load",
  "sources-change",
  "source-change",
  "load-start",
  "abort",
  "error",
  "loaded-metadata",
  "loaded-data",
  "can-play",
  "play",
  "play-fail",
  "pause",
  "playing",
  "seeking",
  "seeked",
  "waiting"
]);

class MediaStateManager extends ComponentController {
  constructor(instance, _request, _media) {
    super(instance);
    this.bf = _request;
    this.k = _media;
    this.cf = _media.$store;
  }
  cf;
  pf = /* @__PURE__ */ new Map();
  wf = true;
  tf = false;
  rf;
  onAttach(el) {
    el.setAttribute("aria-busy", "true");
  }
  onConnect(el) {
    this.Cf();
    this.Df();
    this.Ef();
    this.listen("fullscreen-change", this["fullscreen-change"].bind(this));
    this.listen("fullscreen-error", this["fullscreen-error"].bind(this));
  }
  I(event) {
    const type = event.type;
    this[event.type]?.(event);
    {
      if (TRACKED_EVENT.has(type))
        this.pf.set(type, event);
      this.el?.dispatchEvent(event);
    }
  }
  sf() {
    this.xf();
    this.bf.ef = false;
    this.bf.gf = false;
    this.tf = false;
    this.rf = void 0;
    this.pf.clear();
  }
  qf(request, event) {
    this.bf.Pe.Ue(request, (requestEvent) => {
      event.request = requestEvent;
      appendTriggerEvent(event, requestEvent);
    });
  }
  Cf() {
    this.uf();
    this.yf();
    const textTracks = this.k.textTracks;
    listenEvent(textTracks, "add", this.uf.bind(this));
    listenEvent(textTracks, "remove", this.uf.bind(this));
    listenEvent(textTracks, "mode-change", this.yf.bind(this));
  }
  Df() {
    const qualities = this.k.qualities;
    listenEvent(qualities, "add", this.zf.bind(this));
    listenEvent(qualities, "remove", this.zf.bind(this));
    listenEvent(qualities, "change", this.Ff.bind(this));
    listenEvent(qualities, "auto-change", this.Gf.bind(this));
    listenEvent(qualities, "readonly-change", this.Hf.bind(this));
  }
  Ef() {
    const audioTracks = this.k.audioTracks;
    listenEvent(audioTracks, "add", this.Af.bind(this));
    listenEvent(audioTracks, "remove", this.Af.bind(this));
    listenEvent(audioTracks, "change", this.If.bind(this));
  }
  uf(event) {
    const { textTracks } = this.cf;
    textTracks.set(this.k.textTracks.toArray());
    this.dispatch("text-tracks-change", {
      detail: textTracks(),
      trigger: event
    });
  }
  yf(event) {
    if (event)
      this.qf("textTrack", event);
    const current = this.k.textTracks.selected, { textTrack } = this.cf;
    if (textTrack() !== current) {
      textTrack.set(current);
      this.dispatch("text-track-change", {
        detail: current,
        trigger: event
      });
    }
  }
  Af(event) {
    const { audioTracks } = this.cf;
    audioTracks.set(this.k.audioTracks.toArray());
    this.dispatch("audio-tracks-change", {
      detail: audioTracks(),
      trigger: event
    });
  }
  If(event) {
    const { audioTrack } = this.cf;
    audioTrack.set(this.k.audioTracks.selected);
    this.qf("audioTrack", event);
    this.dispatch("audio-track-change", {
      detail: audioTrack(),
      trigger: event
    });
  }
  zf(event) {
    const { qualities } = this.cf;
    qualities.set(this.k.qualities.toArray());
    this.dispatch("qualities-change", {
      detail: qualities(),
      trigger: event
    });
  }
  Ff(event) {
    const { quality } = this.cf;
    quality.set(this.k.qualities.selected);
    this.qf("quality", event);
    this.dispatch("quality-change", {
      detail: quality(),
      trigger: event
    });
  }
  Gf() {
    this.cf.autoQuality.set(this.k.qualities.auto);
  }
  Hf() {
    this.cf.canSetQuality.set(!this.k.qualities.readonly);
  }
  ["provider-change"](event) {
    this.k.$provider.set(event.detail);
  }
  ["autoplay"](event) {
    appendTriggerEvent(event, this.pf.get("play"));
    appendTriggerEvent(event, this.pf.get("can-play"));
    this.cf.autoplayError.set(void 0);
  }
  ["autoplay-fail"](event) {
    appendTriggerEvent(event, this.pf.get("play-fail"));
    appendTriggerEvent(event, this.pf.get("can-play"));
    this.cf.autoplayError.set(event.detail);
    this.sf();
  }
  ["can-load"](event) {
    this.cf.canLoad.set(true);
    this.pf.set("can-load", event);
    this.qf("load", event);
    this.k.textTracks[TEXT_TRACK_CAN_LOAD]();
  }
  ["media-type-change"](event) {
    appendTriggerEvent(event, this.pf.get("source-change"));
    const viewType = this.cf.viewType();
    this.cf.mediaType.set(event.detail);
    if (viewType !== this.cf.viewType()) {
      setTimeout(
        () => this.dispatch("view-type-change", {
          detail: this.cf.viewType(),
          trigger: event
        }),
        0
      );
    }
  }
  ["stream-type-change"](event) {
    const { streamType, inferredStreamType } = this.cf;
    appendTriggerEvent(event, this.pf.get("source-change"));
    inferredStreamType.set(event.detail);
    event.detail = streamType();
  }
  ["rate-change"](event) {
    this.cf.playbackRate.set(event.detail);
    this.qf("rate", event);
  }
  ["sources-change"](event) {
    this.cf.sources.set(event.detail);
  }
  ["source-change"](event) {
    appendTriggerEvent(event, this.pf.get("sources-change"));
    this.cf.source.set(event.detail);
    this.el?.setAttribute("aria-busy", "true");
    if (this.wf) {
      this.wf = false;
      return;
    }
    this.k.audioTracks[LIST_RESET](event);
    this.k.qualities[LIST_RESET](event);
    this.sf();
    softResetMediaStore(this.k.$store);
    this.pf.set(event.type, event);
  }
  ["abort"](event) {
    appendTriggerEvent(event, this.pf.get("source-change"));
    appendTriggerEvent(event, this.pf.get("can-load"));
  }
  ["load-start"](event) {
    appendTriggerEvent(event, this.pf.get("source-change"));
  }
  ["error"](event) {
    this.cf.error.set(event.detail);
    appendTriggerEvent(event, this.pf.get("abort"));
  }
  ["loaded-metadata"](event) {
    appendTriggerEvent(event, this.pf.get("load-start"));
  }
  ["loaded-data"](event) {
    appendTriggerEvent(event, this.pf.get("load-start"));
  }
  ["can-play"](event) {
    if (event.trigger?.type !== "loadedmetadata") {
      appendTriggerEvent(event, this.pf.get("loaded-metadata"));
    }
    this.Bf(event.detail);
    this.el?.setAttribute("aria-busy", "false");
  }
  ["can-play-through"](event) {
    this.Bf(event.detail);
    appendTriggerEvent(event, this.pf.get("can-play"));
  }
  Bf(detail) {
    const { seekable, seekableEnd, buffered, duration, canPlay } = this.cf;
    seekable.set(detail.seekable);
    buffered.set(detail.buffered);
    duration.set(seekableEnd);
    canPlay.set(true);
  }
  ["duration-change"](event) {
    const { live, duration } = this.cf, time = event.detail;
    if (!live())
      duration.set(!Number.isNaN(time) ? time : 0);
  }
  ["progress"](event) {
    const { buffered, seekable, live, duration, seekableEnd } = this.cf, detail = event.detail;
    buffered.set(detail.buffered);
    seekable.set(detail.seekable);
    if (live()) {
      duration.set(seekableEnd);
      this.dispatch("duration-change", {
        detail: seekableEnd(),
        trigger: event
      });
    }
  }
  ["play"](event) {
    const { paused, autoplayError, ended, autoplaying } = this.cf;
    event.autoplay = autoplaying();
    if (this.bf.gf || !paused()) {
      event.stopImmediatePropagation();
      return;
    }
    appendTriggerEvent(event, this.pf.get("waiting"));
    this.qf("play", event);
    paused.set(false);
    autoplayError.set(void 0);
    if (ended() || this.bf.ef) {
      this.bf.ef = false;
      ended.set(false);
      this.I(this.createEvent("replay", { trigger: event }));
    }
  }
  ["play-fail"](event) {
    appendTriggerEvent(event, this.pf.get("play"));
    this.qf("play", event);
    const { paused, playing } = this.cf;
    paused.set(true);
    playing.set(false);
    this.sf();
  }
  ["playing"](event) {
    const playEvent = this.pf.get("play");
    if (playEvent) {
      appendTriggerEvent(event, this.pf.get("waiting"));
      appendTriggerEvent(event, playEvent);
    } else {
      appendTriggerEvent(event, this.pf.get("seeked"));
    }
    setTimeout(() => this.sf(), 0);
    const { paused, playing, seeking, ended } = this.cf;
    paused.set(false);
    playing.set(true);
    seeking.set(false);
    ended.set(false);
    if (this.bf.gf) {
      event.stopImmediatePropagation();
      this.bf.gf = false;
      return;
    }
    this["started"](event);
  }
  ["started"](event) {
    const { started, live, liveSyncPosition, seekableEnd } = this.cf;
    if (!started()) {
      if (live()) {
        const end = liveSyncPosition() ?? seekableEnd() - 2;
        if (Number.isFinite(end))
          this.k.$provider().currentTime = end;
      }
      started.set(true);
      this.I(this.createEvent("started", { trigger: event }));
    }
  }
  ["pause"](event) {
    if (this.bf.gf) {
      event.stopImmediatePropagation();
      return;
    }
    appendTriggerEvent(event, this.pf.get("seeked"));
    this.qf("pause", event);
    const { paused, playing, seeking } = this.cf;
    paused.set(true);
    playing.set(false);
    seeking.set(false);
    this.sf();
  }
  ["time-update"](event) {
    const { currentTime, played, waiting } = this.cf, detail = event.detail;
    currentTime.set(detail.currentTime);
    played.set(detail.played);
    waiting.set(false);
    for (const track of this.k.textTracks) {
      track[TEXT_TRACK_UPDATE_ACTIVE_CUES](detail.currentTime, event);
    }
  }
  ["volume-change"](event) {
    const { volume, muted } = this.cf, detail = event.detail;
    volume.set(detail.volume);
    muted.set(detail.muted || detail.volume === 0);
    this.qf("volume", event);
  }
  ["seeking"] = functionThrottle(
    (event) => {
      const { seeking, currentTime, paused } = this.cf;
      seeking.set(true);
      currentTime.set(event.detail);
      this.qf("seeking", event);
      if (paused()) {
        this.rf = event;
        this.vf();
      }
    },
    150,
    { leading: true }
  );
  ["seeked"](event) {
    const { seeking, currentTime, paused, duration, ended } = this.cf;
    if (this.bf.$a) {
      seeking.set(true);
      event.stopImmediatePropagation();
    } else if (seeking()) {
      const waitingEvent = this.pf.get("waiting");
      appendTriggerEvent(event, waitingEvent);
      if (waitingEvent?.trigger?.type !== "seeking") {
        appendTriggerEvent(event, this.pf.get("seeking"));
      }
      if (paused())
        this.xf();
      seeking.set(false);
      if (event.detail !== duration())
        ended.set(false);
      currentTime.set(event.detail);
      this.qf("seeked", event);
      const origin = event.originEvent;
      if (origin && origin.isTrusted && !/seek/.test(origin.type)) {
        this["started"](event);
      }
    }
  }
  ["waiting"](event) {
    if (this.tf || this.bf.$a)
      return;
    event.stopImmediatePropagation();
    this.rf = event;
    this.vf();
  }
  vf = functionDebounce(() => {
    if (!this.rf)
      return;
    this.tf = true;
    const { waiting, playing } = this.cf;
    waiting.set(true);
    playing.set(false);
    const event = this.createEvent("waiting", { trigger: this.rf });
    this.pf.set("waiting", event);
    this.el.dispatchEvent(event);
    this.rf = void 0;
    this.tf = false;
  }, 300);
  ["ended"](event) {
    if (this.bf.gf) {
      event.stopImmediatePropagation();
      return;
    }
    const { paused, playing, seeking, ended } = this.cf;
    paused.set(true);
    playing.set(false);
    seeking.set(false);
    ended.set(true);
    this.sf();
  }
  xf() {
    this.vf.cancel();
    this.cf.waiting.set(false);
  }
  ["fullscreen-change"](event) {
    this.cf.fullscreen.set(event.detail);
    this.qf("fullscreen", event);
  }
  ["fullscreen-error"](event) {
    this.qf("fullscreen", event);
  }
  ["picture-in-picture-change"](event) {
    this.cf.pictureInPicture.set(event.detail);
    this.qf("pip", event);
  }
  ["picture-in-picture-error"](event) {
    this.qf("pip", event);
  }
}

class MediaStoreSync extends ComponentController {
  onAttach(el) {
    effect(this.Jf.bind(this));
    effect(this.Kf.bind(this));
    effect(this.Lf.bind(this));
    effect(this.Mf.bind(this));
    effect(this.Nf.bind(this));
    effect(this.Of.bind(this));
    effect(this.Pf.bind(this));
    effect(this.Qf.bind(this));
    effect(this.Rf.bind(this));
    effect(this.Sf.bind(this));
  }
  Tf() {
    return;
  }
  Jf() {
    const autoplay = this.$props.autoplay();
    this.$store.autoplay.set(autoplay);
    this.dispatch("autoplay-change", { detail: autoplay });
  }
  Lf() {
    const loop = this.$props.loop();
    this.$store.loop.set(loop);
    this.dispatch("loop-change", { detail: loop });
  }
  Mf() {
    const controls = this.$props.controls();
    this.$store.controls.set(controls);
    this.dispatch("controls-change", { detail: controls });
  }
  Kf() {
    const poster = this.$props.poster();
    this.$store.poster.set(poster);
    this.dispatch("poster-change", { detail: poster });
  }
  Nf() {
    this.$store.crossorigin.set(this.$props.crossorigin());
  }
  Of() {
    const playsinline = this.$props.playsinline();
    this.$store.playsinline.set(playsinline);
    this.dispatch("playsinline-change", { detail: playsinline });
  }
  Qf() {
    this.dispatch("live-change", { detail: this.$store.live() });
  }
  Pf() {
    this.$store.liveEdgeTolerance.set(this.$props.liveEdgeTolerance());
    this.$store.minLiveDVRWindow.set(this.$props.minLiveDVRWindow());
  }
  Rf() {
    this.dispatch("live-edge-change", { detail: this.$store.liveEdge() });
  }
  Sf() {
    this.$store.thumbnails.set(this.$props.thumbnails());
  }
}

function preconnect(url, rel = "preconnect") {
  const exists = document.querySelector(`link[href="${url}"]`);
  if (!isNull(exists))
    return true;
  const link = document.createElement("link");
  link.rel = rel;
  link.href = url;
  link.crossOrigin = "true";
  document.head.append(link);
  return true;
}
const pendingRequests = {};
function loadScript(src) {
  if (pendingRequests[src])
    return pendingRequests[src].promise;
  const promise = deferredPromise(), exists = document.querySelector(`script[src="${src}"]`);
  if (!isNull(exists)) {
    promise.resolve();
    return promise.promise;
  }
  const script = document.createElement("script");
  script.src = src;
  script.onload = () => {
    promise.resolve();
    delete pendingRequests[src];
  };
  script.onerror = () => {
    promise.reject();
    delete pendingRequests[src];
  };
  setTimeout(() => document.head.append(script), 0);
  return promise.promise;
}
function getRequestCredentials(crossorigin) {
  return crossorigin === "use-credentials" ? "include" : isString(crossorigin) ? "same-origin" : void 0;
}

function findActiveCue(time, cues) {
  for (let i = 0, len = cues.length; i < len; i++) {
    if (isCueActive(cues[i], time))
      return cues[i];
  }
  return null;
}
function isCueActive(cue, time) {
  return time >= cue.startTime && time < cue.endTime;
}
function onTrackChapterChange(tracks, currentTrack, onChange) {
  const track = tracks.toArray().find((track2) => track2.kind === "chapters" && track2.mode === "showing");
  if (track === currentTrack)
    return;
  if (!track) {
    onChange(null);
    return;
  }
  if (track.readyState == 2) {
    onChange(track);
  } else {
    onChange(null);
    track.addEventListener("load", () => onChange(track), { once: true });
  }
}

class TextTrack extends EventsTarget {
  static createId(track) {
    return `id::${track.type}-${track.kind}-${track.src ?? track.label}`;
  }
  src;
  content;
  type;
  encoding;
  id = "";
  label = "";
  language = "";
  kind;
  default = false;
  Ia = false;
  Ea = 0;
  Ga = "disabled";
  Ja = {};
  Ha = [];
  Da = [];
  Fa = [];
  /* @internal */
  [TEXT_TRACK_READY_STATE] = 0;
  /* @internal */
  [TEXT_TRACK_CROSSORIGIN];
  /* @internal */
  [TEXT_TRACK_ON_MODE_CHANGE] = null;
  /* @internal */
  [TEXT_TRACK_NATIVE] = null;
  get metadata() {
    return this.Ja;
  }
  get regions() {
    return this.Ha;
  }
  get cues() {
    return this.Da;
  }
  get activeCues() {
    return this.Fa;
  }
  /**
   * - 0: Not Loading
   * - 1: Loading
   * - 2: Ready
   * - 3: Error
   */
  get readyState() {
    return this[TEXT_TRACK_READY_STATE];
  }
  get mode() {
    return this.Ga;
  }
  set mode(mode) {
    this.setMode(mode);
  }
  constructor(init) {
    super();
    for (const prop of Object.keys(init))
      this[prop] = init[prop];
    if (!this.type)
      this.type = "vtt";
    if (init.content) {
      import('media-captions').then(({ parseText, VTTCue, VTTRegion }) => {
        if (init.type === "json") {
          this.Ka(init.content, VTTCue, VTTRegion);
        } else {
          parseText(init.content, { type: init.type }).then(({ cues, regions }) => {
            this.Da = cues;
            this.Ha = regions;
            this.La();
          });
        }
      });
    } else if (!init.src)
      this[TEXT_TRACK_READY_STATE] = 2;
  }
  addCue(cue, trigger) {
    let i = 0, length = this.Da.length;
    for (i = 0; i < length; i++)
      if (cue.endTime <= this.Da[i].startTime)
        break;
    if (i === length)
      this.Da.push(cue);
    else
      this.Da.splice(i, 0, cue);
    if (trigger?.type !== "cuechange") {
      this[TEXT_TRACK_NATIVE]?.track.addCue(cue);
    }
    this.dispatchEvent(new DOMEvent("add-cue", { detail: cue, trigger }));
    if (isCueActive(cue, this.Ea)) {
      this[TEXT_TRACK_UPDATE_ACTIVE_CUES](this.Ea, trigger);
    }
  }
  removeCue(cue, trigger) {
    const index = this.Da.indexOf(cue);
    if (index >= 0) {
      const isActive = this.Fa.includes(cue);
      this.Da.splice(index, 1);
      this[TEXT_TRACK_NATIVE]?.track.removeCue(cue);
      this.dispatchEvent(new DOMEvent("remove-cue", { detail: cue, trigger }));
      if (isActive) {
        this[TEXT_TRACK_UPDATE_ACTIVE_CUES](this.Ea, trigger);
      }
    }
  }
  setMode(mode, trigger) {
    if (this.Ga === mode)
      return;
    this.Ga = mode;
    if (mode === "disabled") {
      this.Fa = [];
      this.Ma();
    } else if (this.readyState === 2) {
      this[TEXT_TRACK_UPDATE_ACTIVE_CUES](this.Ea, trigger);
    } else {
      this.Na();
    }
    this.dispatchEvent(new DOMEvent("mode-change", { detail: this, trigger }));
    this[TEXT_TRACK_ON_MODE_CHANGE]?.();
  }
  /* @internal */
  [TEXT_TRACK_UPDATE_ACTIVE_CUES](currentTime, trigger) {
    this.Ea = currentTime;
    if (this.mode === "disabled" || !this.Da.length)
      return;
    const activeCues = [];
    for (let i = 0, length = this.Da.length; i < length; i++) {
      const cue = this.Da[i];
      if (isCueActive(cue, currentTime))
        activeCues.push(cue);
    }
    let changed = activeCues.length !== this.Fa.length;
    if (!changed) {
      for (let i = 0; i < activeCues.length; i++) {
        if (!this.Fa.includes(activeCues[i])) {
          changed = true;
          break;
        }
      }
    }
    this.Fa = activeCues;
    if (changed)
      this.Ma(trigger);
  }
  /* @internal */
  [TEXT_TRACK_CAN_LOAD]() {
    this.Ia = true;
    if (this.Ga !== "disabled")
      this.Na();
  }
  async Na() {
    if (!this.Ia || !this.src || this[TEXT_TRACK_READY_STATE] > 0)
      return;
    this[TEXT_TRACK_READY_STATE] = 1;
    this.dispatchEvent(new DOMEvent("load-start"));
    try {
      const { parseResponse, VTTCue, VTTRegion } = await import('media-captions'), crossorigin = this[TEXT_TRACK_CROSSORIGIN]?.();
      const response = fetch(this.src, {
        headers: this.type === "json" ? { "Content-Type": "application/json" } : void 0,
        credentials: getRequestCredentials(crossorigin)
      });
      if (this.type === "json") {
        this.Ka(await (await response).text(), VTTCue, VTTRegion);
      } else {
        const { errors, metadata, regions, cues } = await parseResponse(response, {
          type: this.type,
          encoding: this.encoding
        });
        if (errors[0]?.code === 0) {
          throw errors[0];
        } else {
          this.Ja = metadata;
          this.Ha = regions;
          this.Da = cues;
        }
      }
      this.La();
    } catch (error) {
      this.Oa(error);
    }
  }
  La() {
    this[TEXT_TRACK_READY_STATE] = 2;
    if (!this.src || this.type !== "vtt") {
      const nativeTrack = this[TEXT_TRACK_NATIVE]?.track;
      if (nativeTrack)
        for (const cue of this.Da)
          nativeTrack.addCue(cue);
    }
    const loadEvent = new DOMEvent("load");
    this[TEXT_TRACK_UPDATE_ACTIVE_CUES](this.Ea, loadEvent);
    this.dispatchEvent(loadEvent);
  }
  Oa(error) {
    this[TEXT_TRACK_READY_STATE] = 3;
    this.dispatchEvent(new DOMEvent("error", { detail: error }));
  }
  Ka(json, VTTCue, VTTRegion) {
    try {
      json = JSON.parse(json);
      if (json.regions) {
        this.Ha = json.regions.map((json2) => Object.assign(new VTTRegion(), json2));
      }
      if (json.cues) {
        this.Da = json.cues.filter((json2) => isNumber(json2.startTime) && isNumber(json2.endTime)).map((json2) => Object.assign(new VTTCue(0, 0, ""), json2));
      }
    } catch (error) {
      this.Oa(error);
    }
  }
  Ma(trigger) {
    this.dispatchEvent(new DOMEvent("cue-change", { trigger }));
  }
}
const captionRE = /captions|subtitles/;
function isTrackCaptionKind(track) {
  return captionRE.test(track.kind);
}

class MediaRemoteControl {
  constructor(_logger) {
    this.ma = _logger;
  }
  ka = null;
  ja = null;
  la = -1;
  /**
   * Set the target from which to dispatch media requests events from. The events should bubble
   * up from this target to the `<media-player>` element.
   *
   * @example
   * ```ts
   * const button = document.querySelector('button');
   * remote.setTarget(button);
   * ```
   */
  setTarget(target) {
    this.ka = target;
  }
  /**
   * Returns the current `<media-player>` element. This method will attempt to find the player by
   * searching up from either the given `target` or default target set via `remote.setTarget`.
   *
   * @example
   * ```ts
   * const player = remote.getPlayer();
   * ```
   */
  getPlayer(target) {
    if (this.ja)
      return this.ja;
    (target ?? this.ka)?.dispatchEvent(
      new DOMEvent("find-media-player", {
        detail: (player) => void (this.ja = player),
        bubbles: true,
        composed: true
      })
    );
    return this.ja;
  }
  /**
   * Set the current `<media-player>` element so the remote can support toggle methods such as
   * `togglePaused` as they rely on the current media state.
   */
  setPlayer(player) {
    this.ja = player;
  }
  /**
   * Dispatch a request to start the media loading process. This will only work if the media
   * player has been initialized with a custom loading strategy `<media-player load="custom">`.
   *
   * @docs {@link https://www.vidstack.io/docs/player/core-concepts/loading#loading-strategies}
   */
  startLoading(trigger) {
    this.ia("media-start-loading", trigger);
  }
  /**
   * Dispatch a request to begin/resume media playback.
   */
  play(trigger) {
    this.ia("media-play-request", trigger);
  }
  /**
   * Dispatch a request to pause media playback.
   */
  pause(trigger) {
    this.ia("media-pause-request", trigger);
  }
  /**
   * Dispatch a request to set the media volume to mute (0).
   */
  mute(trigger) {
    this.ia("media-mute-request", trigger);
  }
  /**
   * Dispatch a request to unmute the media volume and set it back to it's previous state.
   */
  unmute(trigger) {
    this.ia("media-unmute-request", trigger);
  }
  /**
   * Dispatch a request to enter fullscreen.
   *
   * @docs {@link https://www.vidstack.io/docs/player/core-concepts/fullscreen#media-remote}
   */
  enterFullscreen(target, trigger) {
    this.ia("media-enter-fullscreen-request", trigger, target);
  }
  /**
   * Dispatch a request to exit fullscreen.
   *
   * @docs {@link https://www.vidstack.io/docs/player/core-concepts/fullscreen#media-remote}
   */
  exitFullscreen(target, trigger) {
    this.ia("media-exit-fullscreen-request", trigger, target);
  }
  /**
   * Dispatch a request to enter picture-in-picture mode.
   *
   * @docs {@link https://www.vidstack.io/docs/player/core-concepts/picture-in-picture#media-remote}
   */
  enterPictureInPicture(trigger) {
    this.ia("media-enter-pip-request", trigger);
  }
  /**
   * Dispatch a request to exit picture-in-picture mode.
   *
   * @docs {@link https://www.vidstack.io/docs/player/core-concepts/picture-in-picture#media-remote}
   */
  exitPictureInPicture(trigger) {
    this.ia("media-exit-pip-request", trigger);
  }
  /**
   * Notify the media player that a seeking process is happening and to seek to the given `time`.
   */
  seeking(time, trigger) {
    this.ia("media-seeking-request", trigger, time);
  }
  /**
   * Notify the media player that a seeking operation has completed and to seek to the given `time`.
   * This is generally called after a series of `remote.seeking()` calls.
   */
  seek(time, trigger) {
    this.ia("media-seek-request", trigger, time);
  }
  seekToLiveEdge(trigger) {
    this.ia("media-live-edge-request", trigger);
  }
  /**
   * Dispatch a request to update the media volume to the given `volume` level which is a value
   * between 0 and 1.
   *
   * @example
   * ```ts
   * remote.changeVolume(0); // 0%
   * remote.changeVolume(0.05); // 5%
   * remote.changeVolume(0.5); // 50%
   * remote.changeVolume(0.75); // 70%
   * remote.changeVolume(1); // 100%
   * ```
   */
  changeVolume(volume, trigger) {
    this.ia("media-volume-change-request", trigger, Math.max(0, Math.min(1, volume)));
  }
  /**
   * Dispatch a request to change the current audio track.
   *
   * @example
   * ```ts
   * remote.changeAudioTrack(1); // track at index 1
   * ```
   */
  changeAudioTrack(index, trigger) {
    this.ia("media-audio-track-change-request", trigger, index);
  }
  /**
   * Dispatch a request to change the video quality. The special value `-1` represents auto quality
   * selection.
   *
   * @example
   * ```ts
   * remote.changeQuality(-1); // auto
   * remote.changeQuality(1); // quality at index 1
   * ```
   */
  changeQuality(index, trigger) {
    this.ia("media-quality-change-request", trigger, index);
  }
  /**
   * Dispatch a request to change the mode of the text track at the given index.
   *
   * @example
   * ```ts
   * remote.changeTextTrackMode(1, 'showing'); // track at index 1
   * ```
   */
  changeTextTrackMode(index, mode, trigger) {
    this.ia("media-text-track-change-request", trigger, {
      index,
      mode
    });
  }
  /**
   * Dispatch a request to change the media playback rate.
   *
   * @example
   * ```ts
   * remote.changePlaybackRate(0.5); // Half the normal speed
   * remote.changePlaybackRate(1); // Normal speed
   * remote.changePlaybackRate(1.5); // 50% faster than normal
   * remote.changePlaybackRate(2); // Double the normal speed
   * ```
   */
  changePlaybackRate(rate, trigger) {
    this.ia("media-rate-change-request", trigger, rate);
  }
  /**
   * Dispatch a request to resume user idle tracking. Refer to {@link MediaRemoteControl.pauseUserIdle}
   * for more information.
   */
  resumeUserIdle(trigger) {
    this.ia("media-resume-user-idle-request", trigger);
  }
  /**
   * Dispatch a request to pause user idle tracking. Pausing tracking will result in the `user-idle`
   * attribute and state being `false` until `remote.resumeUserIdle()` is called. This method
   * is generally used when building custom controls and you'd like to prevent the UI from
   * dissapearing.
   *
   * @example
   * ```ts
   * // Prevent user idling while menu is being interacted with.
   * function onSettingsOpen() {
   *   remote.pauseUserIdle();
   * }
   *
   * function onSettingsClose() {
   *   remote.resumeUserIdle();
   * }
   * ```
   */
  pauseUserIdle(trigger) {
    this.ia("media-pause-user-idle-request", trigger);
  }
  /**
   * Dispatch a request to toggle the media playback state.
   */
  togglePaused(trigger) {
    const player = this.getPlayer(trigger?.target);
    if (!player) {
      return;
    }
    if (player.state.paused)
      this.play(trigger);
    else
      this.pause(trigger);
  }
  /**
   * Dispatch a request to toggle the user idle state.
   */
  toggleUserIdle(trigger) {
    const player = this.getPlayer(trigger?.target);
    if (!player) {
      return;
    }
    player.user.idle(!player.user.idling, 0, trigger);
  }
  /**
   * Dispatch a request to toggle the media muted state.
   */
  toggleMuted(trigger) {
    const player = this.getPlayer(trigger?.target);
    if (!player) {
      return;
    }
    if (player.state.muted)
      this.unmute(trigger);
    else
      this.mute(trigger);
  }
  /**
   * Dispatch a request to toggle the media fullscreen state.
   *
   * @docs {@link https://www.vidstack.io/docs/player/core-concepts/fullscreen#media-remote}
   */
  toggleFullscreen(target, trigger) {
    const player = this.getPlayer(trigger?.target);
    if (!player) {
      return;
    }
    if (player.state.fullscreen)
      this.exitFullscreen(target, trigger);
    else
      this.enterFullscreen(target, trigger);
  }
  /**
   * Dispatch a request to toggle the media picture-in-picture mode.
   *
   * @docs {@link https://www.vidstack.io/docs/player/core-concepts/picture-in-picture#media-remote}
   */
  togglePictureInPicture(trigger) {
    const player = this.getPlayer(trigger?.target);
    if (!player) {
      return;
    }
    if (player.state.pictureInPicture)
      this.exitPictureInPicture(trigger);
    else
      this.enterPictureInPicture(trigger);
  }
  /**
   * Dispatch a request to toggle the current captions mode.
   */
  toggleCaptions(trigger) {
    const player = this.getPlayer(trigger?.target);
    if (!player) {
      return;
    }
    const tracks = player.state.textTracks, track = player.state.textTrack;
    if (track) {
      const index = tracks.indexOf(track);
      this.changeTextTrackMode(index, "disabled", trigger);
      this.la = index;
    } else {
      let index = this.la;
      if (!tracks[index] || !isTrackCaptionKind(tracks[index])) {
        index = -1;
      }
      if (index === -1) {
        index = tracks.findIndex((track2) => isTrackCaptionKind(track2) && track2.default);
      }
      if (index === -1) {
        index = tracks.findIndex((track2) => isTrackCaptionKind(track2));
      }
      if (index >= 0)
        this.changeTextTrackMode(index, "showing", trigger);
      this.la = -1;
    }
  }
  ia(type, trigger, detail) {
    const request = new DOMEvent(type, {
      bubbles: true,
      composed: true,
      detail,
      trigger
    });
    const shouldUsePlayer = trigger?.target && (trigger.target === document || trigger.target === window || trigger.target === document.body || this.ja && !this.ja.contains(trigger.target));
    const target = shouldUsePlayer ? this.ka ?? this.getPlayer() : trigger?.target ?? this.ka;
    target?.dispatchEvent(request);
  }
  na(method) {
  }
}

class ThumbnailsLoader extends ComponentController {
  k;
  onConnect() {
    this.k = useMedia();
    effect(this.Uf.bind(this));
  }
  Uf() {
    const { canLoad, thumbnailCues } = this.k.$store;
    if (!canLoad())
      return;
    const controller = new AbortController(), { crossorigin, thumbnails } = this.k.$store;
    const src = thumbnails();
    if (!src)
      return;
    import('media-captions').then(({ parseResponse }) => {
      parseResponse(
        fetch(src, {
          signal: controller.signal,
          credentials: getRequestCredentials(crossorigin())
        })
      ).then(({ cues }) => thumbnailCues.set(cues)).catch(noop);
    });
    return () => {
      controller.abort();
      thumbnailCues.set([]);
    };
  }
}

class AudioTrackList extends SelectList {
  getById(id) {
    if (id === "")
      return null;
    return this.a.find((track) => track.id === id) ?? null;
  }
}

class NativeTextRenderer {
  priority = 0;
  eg = true;
  aa = null;
  ya = null;
  dg = /* @__PURE__ */ new Set();
  canRender() {
    return true;
  }
  attach(video) {
    this.aa = video;
    video.textTracks.onchange = this.Hc.bind(this);
  }
  addTrack(track) {
    this.dg.add(track);
    this.gg(track);
  }
  removeTrack(track) {
    track[TEXT_TRACK_NATIVE]?.remove?.();
    track[TEXT_TRACK_NATIVE] = null;
    this.dg.delete(track);
  }
  changeTrack(track) {
    const current = track?.[TEXT_TRACK_NATIVE];
    if (current && current.track.mode !== "showing") {
      current.track.mode = "showing";
    }
    this.ya = track;
  }
  setDisplay(display) {
    this.eg = display;
    this.Hc();
  }
  detach() {
    if (this.aa)
      this.aa.textTracks.onchange = null;
    for (const track of this.dg)
      this.removeTrack(track);
    this.dg.clear();
    this.aa = null;
    this.ya = null;
  }
  gg(track) {
    if (!this.aa)
      return;
    const el = track[TEXT_TRACK_NATIVE] ??= this.hg(track);
    if (el instanceof HTMLElement) {
      this.aa.append(el);
      el.track.mode = el.default ? "showing" : "hidden";
    }
  }
  hg(track) {
    const el = document.createElement("track"), isDefault = track.default || track.mode === "showing", isSupported = track.src && track.type === "vtt";
    el.id = track.id;
    el.src = isSupported ? track.src : "https://cdn.jsdelivr.net/npm/vidstack@0.6.12/empty.vtt";
    el.label = track.label;
    el.kind = track.kind;
    el.default = isDefault;
    track.language && (el.srclang = track.language);
    if (isDefault && !isSupported) {
      this.fg(track, el.track);
    }
    return el;
  }
  fg(track, native) {
    if (track.src && track.type === "vtt" || native.cues?.length)
      return;
    for (const cue of track.cues)
      native.addCue(cue);
  }
  Hc(event) {
    for (const track of this.dg) {
      const nativeTrack = track[TEXT_TRACK_NATIVE]?.track;
      if (!nativeTrack)
        continue;
      if (!this.eg) {
        nativeTrack.mode = "disabled";
        continue;
      }
      const isShowing = nativeTrack.mode === "showing";
      if (isShowing)
        this.fg(track, nativeTrack);
      track.setMode(isShowing ? "showing" : "disabled", event);
    }
  }
}

class TextRenderers {
  constructor(_media) {
    this.k = _media;
    const textTracks = _media.textTracks;
    this.$ = textTracks;
    effect(this.ea.bind(this));
    onDispose(this.ba.bind(this));
    listenEvent(textTracks, "add", this.fa.bind(this));
    listenEvent(textTracks, "remove", this.ga.bind(this));
    listenEvent(textTracks, "mode-change", this.Z.bind(this));
  }
  aa = null;
  $;
  _ = [];
  ca = false;
  X = null;
  Y = null;
  ea() {
    const { $store, $iosControls } = this.k;
    this.ca = $store.controls() || $iosControls();
    this.Z();
  }
  add(renderer) {
    this._.push(renderer);
    this.Z();
  }
  remove(renderer) {
    renderer.detach();
    this._.splice(this._.indexOf(renderer), 1);
    this.Z();
  }
  /* @internal */
  [ATTACH_VIDEO](video) {
    requestAnimationFrame(() => {
      this.aa = video;
      if (video) {
        this.X = new NativeTextRenderer();
        this.X.attach(video);
        for (const track of this.$)
          this.da(track);
      }
      this.Z();
    });
  }
  da(track) {
    if (!isTrackCaptionKind(track))
      return;
    this.X?.addTrack(track);
  }
  ha(track) {
    if (!isTrackCaptionKind(track))
      return;
    this.X?.removeTrack(track);
  }
  fa(event) {
    this.da(event.detail);
  }
  ga(event) {
    this.ha(event.detail);
  }
  Z() {
    if (!this.aa) {
      this.ba();
      return;
    }
    const currentTrack = this.$.selected;
    if (this.ca || currentTrack?.[TEXT_TRACK_NATIVE_HLS]) {
      this.Y?.changeTrack(null);
      this.X.setDisplay(true);
      this.X.changeTrack(currentTrack);
      return;
    }
    this.X.setDisplay(false);
    this.X.changeTrack(null);
    if (!currentTrack) {
      this.Y?.changeTrack(null);
      return;
    }
    const customRenderer = this._.sort((a, b) => a.priority - b.priority).find((loader) => loader.canRender(currentTrack));
    if (this.Y !== customRenderer) {
      this.Y?.detach();
      customRenderer?.attach(this.aa);
      this.Y = customRenderer ?? null;
    }
    customRenderer?.changeTrack(currentTrack);
  }
  ba() {
    this.X?.detach();
    this.X = null;
    this.Y?.detach();
    this.Y = null;
  }
}

class TextTrackList extends List {
  Ia = false;
  Pa = {};
  /** @internal */
  [TEXT_TRACK_CROSSORIGIN];
  get selected() {
    const track = this.a.find((t) => t.mode === "showing" && isTrackCaptionKind(t));
    return track ?? null;
  }
  add(init, trigger) {
    const isTrack = init instanceof TextTrack, track = isTrack ? init : new TextTrack(init);
    if (this.Pa[init.kind] && init.default)
      delete init.default;
    track.addEventListener("mode-change", this.Qa);
    this[LIST_ADD](track, trigger);
    track[TEXT_TRACK_CROSSORIGIN] = this[TEXT_TRACK_CROSSORIGIN];
    if (this.Ia)
      track[TEXT_TRACK_CAN_LOAD]();
    if (init.default) {
      this.Pa[init.kind] = track;
      track.mode = "showing";
    }
    return this;
  }
  remove(track, trigger) {
    if (!this.a.includes(track))
      return;
    if (track === this.Pa[track.kind])
      delete this.Pa[track.kind];
    track.mode = "disabled";
    track[TEXT_TRACK_ON_MODE_CHANGE] = null;
    track.removeEventListener("mode-change", this.Qa);
    this[LIST_REMOVE](track, trigger);
    return this;
  }
  clear(trigger) {
    for (const track of this.a)
      this.remove(track, trigger);
    return this;
  }
  getById(id) {
    return this.a.find((track) => track.id === id) ?? null;
  }
  getByKind(kind) {
    const kinds = Array.isArray(kind) ? kind : [kind];
    return this.a.filter((track) => kinds.includes(track.kind));
  }
  /* @internal */
  [TEXT_TRACK_CAN_LOAD]() {
    if (this.Ia)
      return;
    for (const track of this.a)
      track[TEXT_TRACK_CAN_LOAD]();
    this.Ia = true;
  }
  Qa = this.Ra.bind(this);
  Ra(event) {
    const track = event.detail;
    if (track.mode === "showing") {
      const kinds = isTrackCaptionKind(track) ? ["captions", "subtitles"] : [track.kind];
      for (const t of this.a) {
        if (t.mode === "showing" && t != track && kinds.includes(t.kind)) {
          t.mode = "disabled";
        }
      }
    }
    this.dispatchEvent(
      new DOMEvent("mode-change", {
        detail: event.detail,
        trigger: event
      })
    );
  }
}

class SourceSelection {
  constructor(_domSources, _media, _loader) {
    this.T = _domSources;
    this.k = _media;
    this.S = _loader;
    const HLS_LOADER = new HLSProviderLoader(), VIDEO_LOADER = new VideoProviderLoader(), AUDIO_LOADER = new AudioProviderLoader();
    this.Vf = computed(() => {
      return _media.$props.preferNativeHLS() ? [VIDEO_LOADER, AUDIO_LOADER, HLS_LOADER] : [HLS_LOADER, VIDEO_LOADER, AUDIO_LOADER];
    });
    effect(this.Xf.bind(this));
    effect(this.Yf.bind(this));
    effect(this.Zf.bind(this));
    effect(this._f.bind(this));
  }
  Vf;
  Xf() {
    this.k.delegate.R("sources-change", {
      detail: [...normalizeSrc(this.k.$props.src()), ...this.T()]
    });
  }
  Yf() {
    const { $store } = this.k;
    const sources = $store.sources(), currentSource = peek($store.source), newSource = this.Wf(currentSource, sources), noMatch = sources[0]?.src && !newSource.src && !newSource.type;
    if (noMatch) {
      const { crossorigin } = $store, credentials = getRequestCredentials(crossorigin()), abort = new AbortController();
      Promise.all(
        sources.map(
          (source) => isString(source.src) && source.type === "?" ? fetch(source.src, {
            method: "HEAD",
            credentials,
            signal: abort.signal
          }).then((res) => {
            source.type = res.headers.get("content-type") || "??";
            return source;
          }).catch(() => source) : source
        )
      ).then((sources2) => {
        if (abort.signal.aborted)
          return;
        this.Wf(peek($store.source), sources2);
        tick();
      });
      return () => abort.abort();
    }
    tick();
  }
  Wf(currentSource, sources) {
    let newSource = { src: "", type: "" }, newLoader = null;
    for (const src of sources) {
      const loader = peek(this.Vf).find((loader2) => loader2.canPlay(src));
      if (loader) {
        newSource = src;
        newLoader = loader;
      }
    }
    this.$f(currentSource, newSource, newLoader);
    this.ag(peek(this.S), newLoader);
    return newSource;
  }
  $f(currentSource, newSource, newLoader) {
    if (newSource.src === currentSource.src && newSource.type === currentSource.type)
      return;
    this.k.delegate.R("source-change", { detail: newSource });
    this.k.delegate.R("media-type-change", {
      detail: newLoader?.mediaType(newSource) || "unknown"
    });
  }
  ag(currentLoader, newLoader) {
    if (newLoader === currentLoader)
      return;
    this.k.delegate.R("provider-change", { detail: null });
    newLoader && peek(() => newLoader.preconnect?.(this.k));
    this.S.set(newLoader);
    this.k.delegate.R("provider-loader-change", { detail: newLoader });
  }
  Zf() {
    const provider = this.k.$provider();
    if (!provider)
      return;
    if (this.k.$store.canLoad()) {
      peek(
        () => provider.setup({
          ...this.k,
          player: this.k.player
        })
      );
      return;
    }
    peek(() => provider.preconnect?.(this.k));
  }
  _f() {
    const provider = this.k.$provider(), source = this.k.$store.source();
    if (this.k.$store.canLoad()) {
      peek(() => provider?.loadSource(source, peek(this.k.$store.preload)));
      return;
    }
    try {
      isString(source.src) && preconnect(new URL(source.src).origin, "preconnect");
    } catch (e) {
    }
  }
}
function normalizeSrc(src) {
  return (isArray(src) ? src : [!isString(src) && "src" in src ? src : { src }]).map(
    ({ src: src2, type }) => ({
      src: src2,
      type: type ?? (!isString(src2) || src2.startsWith("blob:") ? "video/object" : "?")
    })
  );
}

class Tracks {
  constructor(_domTracks, _media) {
    this.U = _domTracks;
    this.k = _media;
    effect(this.cg.bind(this));
  }
  bg = [];
  cg() {
    const newTracks = [...this.k.$props.textTracks(), ...this.U()];
    for (const oldTrack of this.bg) {
      if (!newTracks.some((t) => t.id === oldTrack.id)) {
        const track = oldTrack.id && this.k.textTracks.getById(oldTrack.id);
        if (track)
          this.k.textTracks.remove(track);
      }
    }
    for (const newTrack of newTracks) {
      const id = newTrack.id || TextTrack.createId(newTrack);
      if (!this.k.textTracks.getById(id)) {
        newTrack.id = id;
        this.k.textTracks.add(newTrack);
      }
    }
    this.bg = newTracks;
  }
}

class Outlet extends Component {
  static el = defineElement({
    tagName: "media-outlet"
  });
  k;
  T = signal([]);
  U = signal([]);
  S = signal(null);
  constructor(instance) {
    super(instance);
    this.k = useMedia();
    new SourceSelection(this.T, this.k, this.S);
    new Tracks(this.U, this.k);
  }
  onAttach(el) {
    el.setAttribute("keep-alive", "");
  }
  onConnect(el) {
    const resize = new ResizeObserver(animationFrameThrottle(this.p.bind(this)));
    resize.observe(el);
    const mutation = new MutationObserver(this.V.bind(this));
    mutation.observe(el, { attributes: true, childList: true });
    if (IS_SAFARI) {
      listenEvent(el, "touchstart", (e) => e.preventDefault(), { passive: false });
    }
    scopedRaf(() => {
      this.p();
      this.V();
    });
    return () => {
      resize.disconnect();
      mutation.disconnect();
    };
  }
  onDestroy() {
    this.k.$store.currentTime.set(0);
  }
  p() {
    const player = this.k.player, width = this.el.offsetWidth, height = this.el.offsetHeight;
    if (!player)
      return;
    player.$store.mediaWidth.set(width);
    player.$store.mediaHeight.set(height);
    setStyle(player, "--media-width", width + "px");
    setStyle(player, "--media-height", height + "px");
  }
  V() {
    const sources = [], tracks = [], children = this.el.children;
    for (const el of children) {
      if (el instanceof HTMLSourceElement) {
        sources.push({
          src: el.src,
          type: el.type
        });
      } else if (el instanceof HTMLTrackElement) {
        tracks.push({
          id: el.id,
          src: el.src,
          kind: el.track.kind,
          language: el.srclang,
          label: el.label,
          default: el.default,
          type: el.getAttribute("data-type")
        });
      }
    }
    this.T.set(sources);
    this.U.set(tracks);
    tick();
  }
  render() {
    let currentProvider;
    onDispose(() => currentProvider?.destroy?.());
    return () => {
      currentProvider?.destroy();
      const loader = this.S();
      if (!loader)
        return null;
      const el = loader.render(this.k.$store);
      {
        peek(() => {
          loader.load(this.k).then((provider) => {
            if (peek(this.S) !== loader)
              return;
            this.k.delegate.R("provider-change", {
              detail: provider
            });
            currentProvider = provider;
          });
        });
      }
      return el;
    };
  }
}

class LibASSTextRenderer {
  constructor(loader, config) {
    this.loader = loader;
    this.config = config;
  }
  priority = 1;
  za = null;
  ya = null;
  Aa = /(ssa|ass)$/;
  canRender(track) {
    return !!track.src && (isString(track.type) && this.Aa.test(track.type) || this.Aa.test(track.src));
  }
  attach(video) {
    this.loader().then(async (mod) => {
      this.za = new mod.default({
        ...this.config,
        video,
        subUrl: this.ya?.src || ""
      });
      listenEvent(this.za, "ready", () => {
        const canvas = this.za?.Ca;
        if (canvas)
          canvas.style.pointerEvents = "none";
      });
      listenEvent(this.za, "error", (event) => {
        if (this.ya) {
          this.ya[TEXT_TRACK_READY_STATE] = 3;
          this.ya.dispatchEvent(
            new DOMEvent("error", {
              trigger: event,
              detail: event.error
            })
          );
        }
      });
    });
  }
  changeTrack(track) {
    if (!track || track.readyState === 3) {
      this.Ba();
    } else if (this.ya !== track) {
      this.za?.setTrackByUrl(track.src);
      this.ya = track;
    }
  }
  detach() {
    this.Ba();
  }
  Ba() {
    this.za?.freeTrack();
    this.ya = null;
  }
}

class ARIAKeyShortcuts extends ComponentController {
  constructor(instance, _shortcut) {
    super(instance);
    this.ig = _shortcut;
  }
  onAttach(el) {
    const { $props, ariaKeys } = useMedia(), keys = el.getAttribute("aria-keyshortcuts");
    if (keys) {
      ariaKeys[this.ig] = keys;
      {
        onDispose(() => {
          delete ariaKeys[this.ig];
        });
      }
      return;
    }
    const shortcuts = $props.keyShortcuts()[this.ig];
    if (shortcuts)
      el.setAttribute("aria-keyshortcuts", shortcuts);
  }
}

export { canUseVideoPresentation as $, ARIAKeyShortcuts as A, MEDIA_ATTRIBUTES as B, canFullscreen as C, MediaRemoteControl as D, MediaRequestContext as E, ENABLE_AUTO_QUALITY as F, TextTrack as G, TEXT_TRACK_READY_STATE as H, IS_SAFARI as I, TEXT_TRACK_ON_MODE_CHANGE as J, SET_AUTO_QUALITY as K, LIST_SELECT as L, MediaStoreFactory as M, LIST_ADD as N, Outlet as O, IS_CHROME as P, coerceToError as Q, loadScript as R, ScreenOrientationController as S, TextTrackList as T, isHLSSupported as U, VideoQualityList as V, LIST_REMOVE as W, canPlayHLSNatively as X, TEXT_TRACK_NATIVE as Y, TEXT_TRACK_NATIVE_HLS as Z, canUsePictureInPicture as _, setAttributeIfEmpty as a, ATTACH_VIDEO as a0, MediaUserController as a1, MEDIA_KEY_SHORTCUTS as a2, List as a3, FullscreenController as a4, softResetMediaStore as a5, TimeRange as a6, getTimeRangesStart as a7, getTimeRangesEnd as a8, LibASSTextRenderer as a9, setARIALabel as b, findActiveCue as c, canChangeVolume as d, onTrackChapterChange as e, functionThrottle as f, isElementParent as g, isCueActive as h, isTrackCaptionKind as i, MediaStoreSync as j, AudioTrackList as k, TEXT_TRACK_CROSSORIGIN as l, mediaPlayerProps as m, TextRenderers as n, onPress as o, preconnect as p, mediaContext as q, MediaKeyboardController as r, scopedRaf as s, ThumbnailsLoader as t, useMedia as u, MediaStateManager as v, MediaRequestManager as w, MediaPlayerDelegate as x, MediaLoadController as y, IS_IPHONE as z };
